import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Drawer, Flex, Modal, Spin, Typography } from "antd";
import { AxiosError } from "axios";
import { Card, getCards } from "entities/card";
import { getProfile } from "entities/viewer";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "shared/lib/antd-global";
import { payout } from "../api";
import { CardAttachButton } from "./card-attach-button";
import styles from "./index.module.scss";

type ReceivePaymentButtonProps = {
  actId: string;
  block?: boolean;
};

export const ReceivePaymentButton = ({
  actId,
  block,
}: ReceivePaymentButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCheckCardModalVisible, setIsCheckCardModalVisible] = useState(false);
  const [isCardPayoutsAlertModalVisible, setIsCardPayoutsAlertModalVisible] =
    useState(false);

  const { data: cards, isLoading } = useQuery({
    queryKey: ["cards"],
    queryFn: () => getCards(),
  });

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
  });

  const { mutate: getPayout, isPending } = useMutation({
    mutationFn: ({ id, cardId }: { id: string; cardId?: string }) =>
      payout(id, cardId ? { card_id: cardId } : undefined),
    onSuccess(response) {
      if (response.success) {
        if (response.form_url) {
          window.open(response.form_url, "_self");
        } else {
          notification.success({ title: t("payment_details.payout_success") });
        }
      } else {
        notification.error({ title: t("payment_details.payment_not_found") });
      }
      setIsModalVisible(false);
      queryClient.invalidateQueries({ queryKey: ["act", actId] });
      queryClient.invalidateQueries({ queryKey: ["acts"] });
    },
    onError(error: AxiosError<{ message: string }>) {
      notification.error({
        title:
          error?.response?.data?.message ||
          t("payment_details.payment_not_found"),
      });
      setIsModalVisible(false);
      queryClient.invalidateQueries({ queryKey: ["act", actId] });
      queryClient.invalidateQueries({ queryKey: ["acts"] });
    },
  });

  const handelReceive = () => {
    if (profile?.features?.wooppay_card_payouts) {
      if (localStorage.getItem("showCardPayoutsAlert")) {
        setIsModalVisible(true);
      } else {
        setIsCardPayoutsAlertModalVisible(true);
      }
    } else {
      getPayout({ id: actId });
    }
  };

  return (
    <>
      <Button
        type="primary"
        onClick={handelReceive}
        loading={isPending || isLoadingProfile}
        disabled={isPending}
        block={block}
      >
        {t("payment_details.receive_payment")}
      </Button>

      <Drawer
        title={t("payment_details.select_card")}
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        footer={null}
        placement="bottom"
        destroyOnHidden
      >
        {isLoading || isPending ? (
          <div className={styles.modalWrapperLoader}>
            <Spin />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100%",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              {cards?.data.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => {
                    setSelectedCard(item);
                    setIsCheckCardModalVisible(true);
                  }}
                  type="link"
                  style={{ marginBottom: 16, padding: 0 }}
                  block
                >
                  {item.pan_masked}
                </Button>
              ))}
            </div>

            <CardAttachButton />

            {/* <Button
              type="primary"
              block
              onClick={() => getPayout({ id: actId })}
              style={{
                marginTop: "auto",
                flex: "0 0 auto",
                marginBottom:
                  "calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
              }}
            >
              {t("payment_details.new_card")}
            </Button> */}
          </div>
        )}
      </Drawer>

      <Modal
        title={
          <Typography.Title level={3}>
            {t("payment_details.payout_alert_title")}
          </Typography.Title>
        }
        open={isCardPayoutsAlertModalVisible}
        onCancel={() => {
          setIsCardPayoutsAlertModalVisible(false);
          localStorage.setItem("showCardPayoutsAlert", "show");
          setIsModalVisible(true);
        }}
        footer={
          <Button
            type="primary"
            onClick={() => {
              setIsCardPayoutsAlertModalVisible(false);
              localStorage.setItem("showCardPayoutsAlert", "show");
              setIsModalVisible(true);
            }}
          >
            {t("payment_details.got_it")}
          </Button>
        }
        destroyOnHidden
      >
        <Typography.Text style={{ display: "block", marginBottom: 12 }}>
          {t("payment_details.payout_alert_intro")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 4 }}>
          {t("payment_details.payout_alert_step1")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 4 }}>
          {t("payment_details.payout_alert_step2")}
        </Typography.Text>
        <Typography.Text style={{ display: "block", marginBottom: 16 }}>
          {t("payment_details.payout_alert_step3")}
        </Typography.Text>
        <Typography.Text style={{ display: "block" }}>
          {t("payment_details.payout_alert_note")}
        </Typography.Text>
      </Modal>

      <Modal
        title={
          <Typography.Title level={4}>
            {t("payment_details.confirm_card_title")}
          </Typography.Title>
        }
        open={isCheckCardModalVisible}
        onCancel={() => {
          setIsCheckCardModalVisible(false);
        }}
        footer={
          <Flex gap="small" justify="end">
            <Button
              onClick={() => {
                setIsCheckCardModalVisible(false);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                getPayout({ id: actId, cardId: selectedCard?.id });
                setIsCheckCardModalVisible(false);
                setIsModalVisible(false);
              }}
            >
              {t("common.confirm")}
            </Button>
          </Flex>
        }
        destroyOnHidden
      >
        <Typography.Text style={{ display: "block", marginBottom: 8 }}>
          {t("payment_details.confirm_card_description")}{" "}
          <Typography.Text
            style={{
              color: "var(--ant-color-primary)",
            }}
          >
            {selectedCard?.pan_masked}
          </Typography.Text>
        </Typography.Text>
      </Modal>
    </>
  );
};
